import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Stack,
  Divider,
  Avatar,
  Tabs,
  Tab,
  TablePagination,
  Slider,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  ExpandMore,
  Payment,
  ArrowBack,
  Refresh,
  AccountBalance,
  Add,
  Description,
  ContentCopy,
  Share,
  PictureAsPdf,
  MoneyOff,
  TrendingUp,
} from '@mui/icons-material';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getDebitsByBorrowers, updateIndividualDebitStatus, convertDebitToInterest, getDebitHistory, writeOffDebit } from '../../services/request/Debits';
import { getBorrowerById } from '../../services/request/Borrowers';
import { createdPayment, getPaymentsByDebit, getAllPayments } from '../../services/request/Payments';
import { formatCurrency } from '../../utils/CurrencyFormat';
import { BorrowerAPIItem } from '../../models/LendingModels';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AmountInput from '../../shared-components/amount-input/AmountInput';
import { PageHeader, MetricStrip, StatusBadge } from '../../shared-components/ui';
import DebitCaptureModal from '../admin/outstandings/DebitCaptureChild';

interface DebitItem {
  id: string;
  amount: number;
  outstanding: number;
  interestRate: number;
  durationType: string;
  startDate: string;
  endDate: string;
  closedAt?: string | null;
  paymentDate: number;
  emiAmount: number;
  emiDate: string;
  status: string;
  type?: string;
  conversionComment?: string;
  writeOffAmount?: number;
  writeOffReason?: string;
}

/** Monthly expected interest on outstanding; matches backend (outstanding/100)×interest_rate. */
function expectedMonthlyInterest(outstanding: number, interestRate: number): number {
  return ((outstanding ?? 0) * (interestRate ?? 0)) / 100;
}

function formatDebitPaymentSchedule(debit: DebitItem): string {
  const pd = debit.paymentDate;
  const dayPart =
    pd != null && !Number.isNaN(Number(pd)) && Number(pd) >= 1 && Number(pd) <= 31
      ? `Day ${pd} of month`
      : 'Payment day not set';
  if (debit.emiDate) {
    try {
      const d = new Date(debit.emiDate);
      if (!Number.isNaN(d.getTime())) {
        return `${dayPart}; Next EMI: ${d.toLocaleDateString('en-IN', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })}`;
      }
    } catch {
      /* ignore */
    }
  }
  return dayPart;
}

type DebitReportRow = {
  rowNum: number;
  idShort: string;
  amount: number;
  outstanding: number;
  expInt: number;
  intLabel: string;
  payDay: string;
  nextEmi: string;
  maturity: string;
  status: string;
};

function mapDebitToReportRow(debit: DebitItem, index: number): DebitReportRow {
  const expInt = expectedMonthlyInterest(debit.outstanding ?? 0, debit.interestRate ?? 0);
  const payDay =
    debit.paymentDate != null &&
    !Number.isNaN(Number(debit.paymentDate)) &&
    Number(debit.paymentDate) >= 1 &&
    Number(debit.paymentDate) <= 31
      ? `Day ${debit.paymentDate}`
      : '—';
  let nextEmi = '—';
  if (debit.emiDate) {
    try {
      const d = new Date(debit.emiDate);
      if (!Number.isNaN(d.getTime())) {
        nextEmi = d.toLocaleDateString('en-IN', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        });
      }
    } catch {
      /* ignore */
    }
  }
  const maturity = debit.endDate
    ? new Date(debit.endDate).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : '—';
  return {
    rowNum: index + 1,
    idShort: (debit.id || '').substring(0, 8),
    amount: debit.amount ?? 0,
    outstanding: debit.outstanding ?? 0,
    expInt,
    intLabel: debit.interestRate != null ? `${debit.interestRate}%` : 'N/A',
    payDay,
    nextEmi,
    maturity,
    status: debit.status || 'N/A',
  };
}

const CustomerDebitsView: React.FC = () => {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  
  const [customer, setCustomer] = useState<BorrowerAPIItem | null>(null);
  const [debits, setDebits] = useState<DebitItem[]>([]);
  const [totalOutstanding, setTotalOutstanding] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedDebit, setExpandedDebit] = useState<string | null>(null);
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [selectedDebitId, setSelectedDebitId] = useState<string | null>(null);
  const [paymentForm, setPaymentForm] = useState({
    paymentType: '',
    amount: '',
    note: '',
    paymentReceivedBy: '',
    creditScore: '' as string,
  });
  const [payments, setPayments] = useState<{ [key: string]: any[] }>({});
  const [loadingPayments, setLoadingPayments] = useState<{ [key: string]: boolean }>({});
  const [updatingStatus, setUpdatingStatus] = useState<{ [key: string]: boolean }>({});
  const [tabValue, setTabValue] = useState(0);
  const [allPayments, setAllPayments] = useState<any[]>([]);
  const [loadingAllPayments, setLoadingAllPayments] = useState(false);
  const [paymentPage, setPaymentPage] = useState(0);
  const [paymentRowsPerPage, setPaymentRowsPerPage] = useState(10);
  const [totalPayments, setTotalPayments] = useState(0);
  const [openCaptureDebitDialog, setOpenCaptureDebitDialog] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('ACTIVE');
  const [debitPage, setDebitPage] = useState(0);
  const [debitRowsPerPage, setDebitRowsPerPage] = useState(20);
  const [totalDebits, setTotalDebits] = useState(0);
  const [reportPreviewOpen, setReportPreviewOpen] = useState(false);
  const [reportText, setReportText] = useState('');
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [convertDebitId, setConvertDebitId] = useState<string | null>(null);
  const [convertDebitAmount, setConvertDebitAmount] = useState<number | null>(null);
  const [convertForm, setConvertForm] = useState({
    interestRate: '',
    conversionComment: '',
    startDate: '',
    endDate: '',
    paymentDate: '',
  });
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyEvents, setHistoryEvents] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [writeOffDialogOpen, setWriteOffDialogOpen] = useState(false);
  const [writeOffDebitId, setWriteOffDebitId] = useState<string | null>(null);
  const [writeOffOutstanding, setWriteOffOutstanding] = useState<number>(0);
  const [writeOffSubmitting, setWriteOffSubmitting] = useState(false);
  const [writeOffForm, setWriteOffForm] = useState({
    reason: '',
    blacklistBorrower: false,
    blacklistReason: '',
  });

  const outstandingReportHeader = useMemo(() => {
    if (!customer || !debits.length) return null;
    let totalAmount = 0;
    let totalOutstandingLocal = 0;
    let totalExpectedInterest = 0;
    const interestRates = new Set<number>();
    debits.forEach((debit) => {
      totalAmount += debit.amount || 0;
      totalOutstandingLocal += debit.outstanding || 0;
      totalExpectedInterest += expectedMonthlyInterest(
        debit.outstanding ?? 0,
        debit.interestRate ?? 0
      );
      if (typeof debit.interestRate === 'number') {
        interestRates.add(debit.interestRate);
      }
    });
    const maturityTimes = debits
      .map((d) => (d.endDate ? new Date(d.endDate).getTime() : NaN))
      .filter((t) => !Number.isNaN(t));
    const earliestMaturity =
      maturityTimes.length > 0 ? new Date(Math.min(...maturityTimes)) : null;
    const nearestDueDateStr = earliestMaturity
      ? earliestMaturity.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })
      : 'Not available';
    const ratesStr =
      interestRates.size > 0
        ? Array.from(interestRates)
            .sort((a, b) => a - b)
            .map((r) => `${r}%`)
            .join(', ')
        : 'Not available';
    return {
      customerName: customer.name,
      totalAmount,
      totalOutstandingLocal,
      totalExpectedInterest,
      nearestDueDateStr,
      ratesStr,
    };
  }, [customer, debits]);

  useEffect(() => {
    if (customerId) {
      fetchCustomerData();
      fetchDebits();
    }
  }, [customerId]);

  useEffect(() => {
    if (customerId && tabValue === 0) {
      fetchDebits();
    }
  }, [statusFilter, debitPage, debitRowsPerPage, tabValue]);

  useEffect(() => {
    if (tabValue === 1) {
      fetchAllPayments();
    }
  }, [tabValue, paymentPage, paymentRowsPerPage]);

  const fetchCustomerData = async () => {
    if (!customerId) return;
    try {
      const res = await getBorrowerById(customerId);
      if (res && (res as BorrowerAPIItem).id) {
        setCustomer(res as BorrowerAPIItem);
      }
    } catch (err) {
      console.error('Error fetching customer:', err);
    }
  };

  const fetchDebits = async () => {
    if (!customerId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Build filter object - only include status if not 'ALL'
      const filterObj: any = {};
      if (statusFilter && statusFilter !== 'ALL') {
        filterObj.status = statusFilter;
      }
      
      const response = await getDebitsByBorrowers(customerId, debitPage, debitRowsPerPage, filterObj);
      
      if (response.data && response.data.code === 200) {
        setDebits(response.data.result || []);
        // Set total outstanding from debit-insights API
        setTotalOutstanding(response.data.totalOutstanding || 0);
        // Set total count for pagination
        setTotalDebits(response.data.totalCount || response.data.result?.length || 0);
      } else {
        setDebits([]);
        setTotalOutstanding(0);
        setTotalDebits(0);
      }
    } catch (err) {
      setError('Failed to fetch debits');
      console.error('Error fetching debits:', err);
      setDebits([]);
      setTotalDebits(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentsForDebit = async (debitId: string) => {
    try {
      setLoadingPayments(prev => ({ ...prev, [debitId]: true }));
      const response = await getPaymentsByDebit(debitId, 0, 50);
      
      if (response.data && response.data.code === 200) {
        setPayments(prev => ({ ...prev, [debitId]: response.data.result || [] }));
      }
    } catch (err) {
      console.error('Error fetching payments:', err);
    } finally {
      setLoadingPayments(prev => ({ ...prev, [debitId]: false }));
    }
  };

  const handleDebitExpand = (debitId: string) => {
    const isExpanding = expandedDebit !== debitId;
    setExpandedDebit(isExpanding ? debitId : null);
    
    if (isExpanding && !payments[debitId]) {
      fetchPaymentsForDebit(debitId);
    }
  };

  const handleOpenPaymentDialog = (debitId: string) => {
    setSelectedDebitId(debitId);
    setPaymentForm({
      paymentType: '',
      amount: '',
      note: '',
      paymentReceivedBy: '',
      creditScore: '',
    });
    setOpenPaymentDialog(true);
  };

  const handleClosePaymentDialog = () => {
    setOpenPaymentDialog(false);
    setSelectedDebitId(null);
    setPaymentForm({
      paymentType: '',
      amount: '',
      note: '',
      paymentReceivedBy: '',
      creditScore: '',
    });
  };

  const handlePaymentFormChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    setPaymentForm(prev => ({ ...prev, [name as string]: value }));
  };

  const handleSubmitPayment = async () => {
    if (!selectedDebitId) return;

    // Validation
    if (!paymentForm.paymentType) {
      toast.error('Please select a payment type');
      return;
    }

    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    try {
      setLoading(true);
      const payload: Record<string, string | number> = {
        paymentType: paymentForm.paymentType,
        amount: paymentForm.amount,
        note: paymentForm.note,
        paymentReceivedBy: paymentForm.paymentReceivedBy,
      };
      if (paymentForm.creditScore !== '' && paymentForm.creditScore !== undefined) {
        payload.creditScore = Number(paymentForm.creditScore);
      }
      const response = await createdPayment(selectedDebitId, payload);

      if (response.status === 200 && response.data.paymentId) {
        toast.success('Payment captured successfully! 🎉');
        handleClosePaymentDialog();
        // Refresh debits and payments
        await fetchDebits();
        await fetchPaymentsForDebit(selectedDebitId);
      } else {
        toast.error('Failed to capture payment');
      }
    } catch (err) {
      toast.error('Error capturing payment');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (debitId: string, newStatus: string, currentStatus: string) => {
    if (newStatus === currentStatus) return;

    try {
      setUpdatingStatus(prev => ({ ...prev, [debitId]: true }));
      const response = await updateIndividualDebitStatus(debitId, newStatus);

      if (response.status === 200 || (response.data && response.data.code === 200)) {
        toast.success(`Status updated to ${newStatus} successfully! ✅`);
        // Refresh debits list
        await fetchDebits();
      } else {
        toast.error('Failed to update status');
      }
    } catch (err) {
      toast.error('Error updating status');
      console.error('Error:', err);
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [debitId]: false }));
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'OPEN': return 'info';
      case 'CLOSED': return 'default';
      case 'CREATED': return 'warning';
      case 'ACTIVE': return 'success';
      case 'INACTIVE': return 'error';
      case 'COMPLETED': return 'success';
      case 'WRITTEN_OFF': return 'error';
      default: return 'default';
    }
  };

  const canWriteOffDebit = (debit: DebitItem) => {
    const st = (debit.status || '').toUpperCase();
    if (st === 'CLOSED' || st === 'WRITTEN_OFF') return false;
    return (debit.outstanding || 0) > 0;
  };

  const handleOpenWriteOff = (debit: DebitItem) => {
    setWriteOffDebitId(debit.id);
    setWriteOffOutstanding(debit.outstanding || 0);
    setWriteOffForm({ reason: '', blacklistBorrower: false, blacklistReason: '' });
    setWriteOffDialogOpen(true);
  };

  const handleCloseWriteOff = () => {
    setWriteOffDialogOpen(false);
    setWriteOffDebitId(null);
    setWriteOffOutstanding(0);
    setWriteOffForm({ reason: '', blacklistBorrower: false, blacklistReason: '' });
  };

  const handleSubmitWriteOff = async () => {
    if (!writeOffDebitId) return;
    const reason = writeOffForm.reason.trim();
    if (!reason) {
      toast.error('Please enter a write-off reason');
      return;
    }
    setWriteOffSubmitting(true);
    try {
      const payload: { reason: string; blacklistBorrower?: boolean; blacklistReason?: string } = {
        reason,
        blacklistBorrower: writeOffForm.blacklistBorrower || undefined,
      };
      if (writeOffForm.blacklistBorrower && writeOffForm.blacklistReason.trim()) {
        payload.blacklistReason = writeOffForm.blacklistReason.trim();
      }
      const res = await writeOffDebit(writeOffDebitId, payload);
      if (res?.statusCode === 200) {
        toast.success(res?.message || 'Debit written off');
        handleCloseWriteOff();
        await fetchDebits();
        await fetchCustomerData();
      } else {
        toast.error(res?.message || 'Write-off failed');
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Write-off failed';
      toast.error(msg);
    } finally {
      setWriteOffSubmitting(false);
    }
  };

  const calculateProgress = (outstanding: number, amount: number) => {
    if (amount === 0) return 0;
    return ((amount - outstanding) / amount) * 100;
  };

  const fetchAllPayments = async () => {
    try {
      setLoadingAllPayments(true);
      const response = await getAllPayments(paymentPage, paymentRowsPerPage);
      
      if (response.data && response.data.code === 200) {
        setAllPayments(response.data.result || []);
        setTotalPayments(response.data.totalCount || 0);
      } else if (response.status === 200 && response.data) {
        setAllPayments(response.data.result || []);
        setTotalPayments(response.data.totalCount || 0);
      }
    } catch (err) {
      console.error('Error fetching all payments:', err);
      toast.error('Failed to fetch payments');
    } finally {
      setLoadingAllPayments(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPaymentPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPaymentRowsPerPage(parseInt(event.target.value, 10));
    setPaymentPage(0);
  };

  const handleOpenCaptureDebitDialog = () => {
    setOpenCaptureDebitDialog(true);
  };

  const handleCloseCaptureDebitDialog = () => {
    setOpenCaptureDebitDialog(false);
  };

  const handleDebitCreated = () => {
    // Refresh the debits list after debit is created
    fetchDebits();
    toast.success('Debit added successfully! 🎉');
  };

  const handleStatusFilterChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    setStatusFilter(event.target.value as string);
    setDebitPage(0); // Reset to first page when filter changes
  };

  const handleOpenConvertDialog = (debit: DebitItem) => {
    setConvertDebitId(debit.id);
    setConvertDebitAmount(debit.amount || 0);
    setConvertForm({
      interestRate: debit.interestRate ? String(debit.interestRate) : '',
      conversionComment: debit.conversionComment || '',
      startDate: debit.startDate ? String(debit.startDate).substring(0, 10) : '',
      endDate: debit.endDate ? String(debit.endDate).substring(0, 10) : '',
      paymentDate: debit.paymentDate ? String(debit.paymentDate) : '',
    });
    setConvertDialogOpen(true);
  };

  const handleCloseConvertDialog = () => {
    setConvertDialogOpen(false);
    setConvertDebitId(null);
    setConvertDebitAmount(null);
    setConvertForm({
      interestRate: '',
      conversionComment: '',
      startDate: '',
      endDate: '',
      paymentDate: '',
    });
  };

  const handleConvertFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setConvertForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitConvert = async () => {
    if (!convertDebitId) return;
    const rate = parseFloat(convertForm.interestRate);
    if (isNaN(rate) || rate <= 0) {
      toast.error('Please enter a valid interest rate');
      return;
    }

    try {
      const payload: { interestRate: number; conversionComment?: string; startDate?: string; endDate?: string; paymentDate?: number } = {
        interestRate: rate,
      };
      if (convertForm.conversionComment) {
        payload.conversionComment = convertForm.conversionComment;
      }
      if (convertForm.startDate) {
        payload.startDate = convertForm.startDate;
      }
       if (convertForm.endDate) {
         payload.endDate = convertForm.endDate;
       }
       if (convertForm.paymentDate) {
         const paymentDay = parseInt(convertForm.paymentDate, 10);
         if (!isNaN(paymentDay)) {
           payload.paymentDate = paymentDay;
         }
       }

      const response = await convertDebitToInterest(convertDebitId, payload);
      if (response.status === 200 && response.data && response.data.code === 200) {
        toast.success('Debit converted to interest successfully! 🎉');
      } else {
        toast.success('Debit converted to interest successfully! 🎉');
      }
      handleCloseConvertDialog();
      await fetchDebits();
    } catch (err) {
      console.error('Error converting debit:', err);
      toast.error('Failed to convert debit. Please try again.');
    }
  };

  const handleOpenHistory = async (debitId: string) => {
    setHistoryDialogOpen(true);
    setHistoryLoading(true);
    try {
      const response = await getDebitHistory(debitId);
      const events = response.result || response || [];
      setHistoryEvents(events);
    } catch (err) {
      console.error('Error fetching debit history:', err);
      toast.error('Failed to load debit history');
      setHistoryEvents([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleDebitPageChange = (event: unknown, newPage: number) => {
    setDebitPage(newPage);
  };

  const handleDebitRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDebitRowsPerPage(parseInt(event.target.value, 10));
    setDebitPage(0); // Reset to first page when page size changes
  };

  const buildOutstandingReportText = () => {
    if (!customer) {
      return 'No customer selected.';
    }

    if (!debits.length) {
      return `No outstanding debits found for ${customer.name}.`;
    }

    let totalAmount = 0;
    let totalOutstandingLocal = 0;
    let totalExpectedInterest = 0;
    let nearestDueDate: Date | null = null;
    const interestRates = new Set<number>();

    debits.forEach((debit) => {
      totalAmount += debit.amount || 0;
      totalOutstandingLocal += debit.outstanding || 0;
      totalExpectedInterest += expectedMonthlyInterest(
        debit.outstanding ?? 0,
        debit.interestRate ?? 0
      );

      if (debit.endDate) {
        const due = new Date(debit.endDate);
        if (!nearestDueDate || due < nearestDueDate) {
          nearestDueDate = due;
        }
      }

      if (typeof debit.interestRate === 'number') {
        interestRates.add(debit.interestRate);
      }
    });

    const lines: string[] = [];
    lines.push(`Customer Outstanding Report - ${customer.name}`);
    lines.push('');
    lines.push(`Total Debit Amount: ${formatCurrency(totalAmount)}`);
    lines.push(`Current Outstanding: ${formatCurrency(totalOutstandingLocal)}`);
    lines.push(`Total expected monthly interest: ${formatCurrency(totalExpectedInterest)}`);

    const nearestDueDateStr = nearestDueDate
      ? (nearestDueDate as Date).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })
      : 'Not available';

    lines.push(`Nearest Due Date: ${nearestDueDateStr}`);

    if (interestRates.size > 0) {
      const rates = Array.from(interestRates)
        .sort((a, b) => a - b)
        .map((r) => `${r}%`)
        .join(', ');
      lines.push(`Interest Rate(s): ${rates}`);
    } else {
      lines.push('Interest Rate(s): Not available');
    }

    lines.push('');
    lines.push('Debit-wise breakdown:');
    debits.forEach((debit, index) => {
      const dueStr = debit.endDate
        ? new Date(debit.endDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
        : 'Not set';
      const expInt = expectedMonthlyInterest(debit.outstanding ?? 0, debit.interestRate ?? 0);
      const ratePct = debit.interestRate != null ? `${debit.interestRate}%` : 'N/A';
      const paySchedule = formatDebitPaymentSchedule(debit);
      lines.push(
        `${index + 1}. Amount: ${formatCurrency(debit.amount)}, ` +
          `Outstanding: ${formatCurrency(debit.outstanding)}, ` +
          `Expected interest (monthly): ${formatCurrency(expInt)}, ` +
          `Interest %: ${ratePct}, ` +
          `Payment: ${paySchedule}, ` +
          `Maturity: ${dueStr}`
      );
    });

    return lines.join('\n');
  };

  const handleOpenOutstandingReportPreview = () => {
    const text = buildOutstandingReportText();
    setReportText(text);
    setReportPreviewOpen(true);
  };

  const handleCopyOutstandingReport = async () => {
    try {
      const text = reportText || buildOutstandingReportText();
      setReportText(text);
      await navigator.clipboard.writeText(text);
      toast.success('Outstanding report copied to clipboard!', { position: 'bottom-center' });
    } catch (err) {
      console.error('Failed to copy report:', err);
      toast.error('Failed to copy report. Please try again.', { position: 'bottom-center' });
    }
  };

  const handleShareOutstandingReport = async () => {
    try {
      const text = reportText || buildOutstandingReportText();
      setReportText(text);

      if (navigator.share) {
        await navigator.share({
          title: `Customer Outstanding Report - ${customer?.name ?? ''}`,
          text,
        });
        toast.success('Outstanding report shared!', { position: 'bottom-center' });
      } else {
        await navigator.clipboard.writeText(text);
        toast.info('Share not available. Report copied to clipboard instead.', {
          position: 'bottom-center',
        });
      }
    } catch (err: any) {
      if (err && err.name === 'AbortError') {
        return;
      }

      console.error('Failed to share report:', err);
      try {
        const text = buildOutstandingReportText();
        setReportText(text);
        await navigator.clipboard.writeText(text);
        toast.info('Share failed. Report copied to clipboard instead.', {
          position: 'bottom-center',
        });
      } catch {
        toast.error('Failed to share or copy report. Please try again.', {
          position: 'bottom-center',
        });
      }
    }
  };

  const handleDownloadOutstandingReportPdf = () => {
    if (!customer || !debits.length) {
      toast.error('No data to export', { position: 'bottom-center' });
      return;
    }

    const formatAmountForPdf = (value: number) =>
      `INR ${value.toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

    doc.setFontSize(14);
    doc.text(`Customer Outstanding Report - ${customer.name}`, 14, 18);

    doc.setFontSize(10);
    const totalAmount = debits.reduce((sum, d) => sum + (d.amount || 0), 0);
    const totalOutstandingLocal = debits.reduce((sum, d) => sum + (d.outstanding || 0), 0);
    const totalExpectedInterest = debits.reduce(
      (sum, d) => sum + expectedMonthlyInterest(d.outstanding ?? 0, d.interestRate ?? 0),
      0
    );
    const summaryLines = [
      `Total Debit Amount: ${formatAmountForPdf(totalAmount)}`,
      `Current Outstanding: ${formatAmountForPdf(totalOutstandingLocal)}`,
      `Total expected monthly interest: ${formatAmountForPdf(totalExpectedInterest)}`,
    ];
    const marginX = 14;
    const summaryLineHeightMm = 6;
    let summaryY = 26;
    summaryLines.forEach((line) => {
      doc.text(line, marginX, summaryY);
      summaryY += summaryLineHeightMm;
    });
    const tableStartY = summaryY + 8;

    const body = debits.map((debit, index) => {
      const r = mapDebitToReportRow(debit, index);
      return [
        r.rowNum,
        r.idShort,
        formatAmountForPdf(r.amount),
        formatAmountForPdf(r.outstanding),
        formatAmountForPdf(r.expInt),
        r.intLabel,
        r.payDay,
        r.nextEmi,
        r.maturity,
        r.status,
      ];
    });

    const pageW = doc.internal.pageSize.getWidth();
    autoTable(doc, {
      head: [
        [
          '#',
          'Debit ID',
          'Total',
          'Outstanding',
          'Exp. int/mo',
          'Int %',
          'Pay day',
          'Next EMI',
          'Maturity',
          'Status',
        ],
      ],
      body,
      startY: tableStartY,
      margin: { left: marginX, right: marginX },
      tableWidth: pageW - 2 * marginX,
      styles: {
        fontSize: 8,
        cellPadding: 2,
        valign: 'middle',
        overflow: 'linebreak',
      },
      headStyles: {
        fillColor: [25, 118, 210],
        textColor: 255,
        halign: 'center',
        valign: 'middle',
      },
      columnStyles: {
        0: { halign: 'center' },
        1: { halign: 'left' },
        2: { halign: 'right' },
        3: { halign: 'right' },
        4: { halign: 'right' },
        5: { halign: 'center' },
        6: { halign: 'center' },
        7: { halign: 'center' },
        8: { halign: 'center' },
        9: { halign: 'center' },
      },
      alternateRowStyles: { fillColor: [248, 248, 248] },
    });

    const fileName = `outstanding_report_${customer.name.replace(/\s+/g, '_')}.pdf`;
    doc.save(fileName);
  };

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <ToastContainer position="top-right" autoClose={3000} />

      <PageHeader
        eyebrow="Lending · Ops"
        title={customer?.name ? customer.name : 'Customer debits'}
        subtitle={
          customer
            ? `Debit book · ID ${customer.id.substring(0, 8)}… · compare end date vs closed date on each loan`
            : 'Loading customer…'
        }
        actions={
          <>
            <IconButton onClick={() => window.close()} color="primary" aria-label="Back">
              <ArrowBack />
            </IconButton>
            <Button variant="contained" startIcon={<Add />} onClick={handleOpenCaptureDebitDialog}>
              Add debit
            </Button>
            <Button variant="outlined" startIcon={<Refresh />} onClick={fetchDebits} disabled={loading}>
              Refresh
            </Button>
          </>
        }
      />

      <Box sx={{ display: 'none' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => window.close()} color="primary">
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h4" component="h1">
              Customer Debits
            </Typography>
            {customer && (
              <Typography variant="body2" color="text.secondary">
                {customer.name} - ID: {customer.id.substring(0, 8)}...
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenCaptureDebitDialog}
            color="primary"
          >
            Add Debit
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchDebits}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {customer && (
        <MetricStrip
          columns={{ xs: 12, sm: 6, md: 3 }}
          metrics={[
            {
              label: 'Credit status',
              value: <StatusBadge status={customer.creditStatus} />,
              hint: customer.blacklisted ? 'Blacklisted' : 'On book',
              tone: customer.blacklisted ? 'error' : 'success',
            },
            {
              label: 'Total outstanding',
              value: formatCurrency(totalOutstanding),
              hint: 'From debit insights',
              tone: 'error',
            },
            {
              label: 'Debits',
              value: debits.length,
              hint: 'In current filter',
            },
            {
              label: 'Comments',
              value: customer.comments || '—',
              hint: 'Internal notes',
            },
          ]}
        />
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="All Debits" />
          <Tab label="Payment History" />
        </Tabs>
      </Box>

      {/* Tab Panel: Debits */}
      {tabValue === 0 && (
        <Box>
          {/* Status Filter */}
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Filter by Status</InputLabel>
              <Select
                value={statusFilter}
                label="Filter by Status"
                onChange={handleStatusFilterChange as any}
              >
                <MenuItem value="ALL">All Statuses</MenuItem>
                <MenuItem value="ACTIVE">Active (default)</MenuItem>
                <MenuItem value="OPEN">Open</MenuItem>
                <MenuItem value="CLOSED">Closed</MenuItem>
                <MenuItem value="CREATED">Created</MenuItem>
                <MenuItem value="INACTIVE">Inactive</MenuItem>
                <MenuItem value="COMPLETED">Completed</MenuItem>
                <MenuItem value="WRITTEN_OFF">Written off</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary">
              Showing {debits.length} of {totalDebits} debits
            </Typography>
          </Box>

          {debits.length === 0 && !loading ? (
            <Alert severity="info">
              {totalDebits === 0 
                ? 'No debits found for this customer.'
                : `No debits found with status "${statusFilter}".`}
            </Alert>
          ) : (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {debits.map((debit) => (
            <Accordion 
              key={debit.id}
              expanded={expandedDebit === debit.id}
              onChange={() => handleDebitExpand(debit.id)}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={2}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Amount</Typography>
                      <Typography variant="h6" color="primary">
                        {formatCurrency(debit.amount)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Outstanding</Typography>
                      <Typography variant="h6" color="error">
                        {formatCurrency(debit.outstanding)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Interest Rate</Typography>
                      <Typography variant="body1">
                        {debit.interestRate}%
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">EMI</Typography>
                      <Typography variant="body1">
                        {formatCurrency(debit.emiAmount)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={1.5}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Duration</Typography>
                      <Typography variant="body1">
                        {debit.durationType}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={1.5}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Payment Date</Typography>
                      <Typography variant="body1" fontWeight="bold" color="warning.main">
                        Day {debit.paymentDate}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={1}>
                    <Chip 
                      label={debit.status} 
                      color={getStatusColor(debit.status) as any}
                      size="small"
                    />
                  </Grid>
                  {debit.type === 'SHORT_TERM_DEBIT' && (
                    <Grid item xs={12} md={2}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleOpenConvertDialog(debit)}
                      >
                        Convert to Interest
                      </Button>
                    </Grid>
                  )}
                  {canWriteOffDebit(debit) && (
                    <Grid item xs={12} md={2}>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<MoneyOff />}
                        onClick={() => handleOpenWriteOff(debit)}
                      >
                        Write off
                      </Button>
                    </Grid>
                  )}
                  <Grid item xs={12} md={2}>
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => handleOpenHistory(debit.id)}
                    >
                      View History
                    </Button>
                  </Grid>
                </Grid>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  <Divider sx={{ mb: 2 }} />
                  
                  {/* Debit Details */}
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="h6" gutterBottom>Debit Information</Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">Start Date</Typography>
                              <Typography variant="body1">{debit.startDate || 'N/A'}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">End Date</Typography>
                              <Typography variant="body1">{debit.endDate || 'N/A'}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">Closed Date</Typography>
                              <Typography variant="body1">
                                {debit.closedAt
                                  ? new Date(debit.closedAt).toLocaleDateString('en-IN', {
                                      day: 'numeric',
                                      month: 'short',
                                      year: 'numeric',
                                    })
                                  : (debit.status || '').toUpperCase() === 'CLOSED'
                                    ? 'N/A'
                                    : '—'}
                              </Typography>
                              {debit.closedAt && debit.endDate && (() => {
                                const end = new Date(debit.endDate);
                                const closed = new Date(debit.closedAt);
                                end.setHours(0, 0, 0, 0);
                                closed.setHours(0, 0, 0, 0);
                                const days = Math.round((closed.getTime() - end.getTime()) / (1000 * 60 * 60 * 24));
                                if (Number.isNaN(days) || days === 0) {
                                  return (
                                    <Typography variant="caption" color="text.secondary">
                                      Closed on end date
                                    </Typography>
                                  );
                                }
                                return (
                                  <Typography variant="caption" color={days < 0 ? 'success.main' : 'warning.main'}>
                                    {days < 0
                                      ? `${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} before end date`
                                      : `${days} day${days === 1 ? '' : 's'} after end date`}
                                  </Typography>
                                );
                              })()}
                            </Grid>
                            <Grid item xs={12}>
                              <Box sx={{ 
                                bgcolor: 'warning.light', 
                                p: 2, 
                                borderRadius: 2, 
                                border: '2px solid',
                                borderColor: 'warning.main'
                              }}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                  Monthly Payment Due Date
                                </Typography>
                                <Typography variant="h5" color="warning.dark" fontWeight="bold">
                                  Day {debit.paymentDate} of every month
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Interest payment should be made on this date
                                </Typography>
                              </Box>
                            </Grid>
                            {debit.type === 'INTEREST' && debit.conversionComment && (
                              <Grid item xs={12}>
                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="body2" color="text.secondary">
                                    Conversion Comment
                                  </Typography>
                                  <Typography variant="body1">
                                    {debit.conversionComment}
                                  </Typography>
                                </Box>
                              </Grid>
                            )}
                            {(debit.status || '').toUpperCase() === 'WRITTEN_OFF' && (
                              <Grid item xs={12}>
                                <Alert severity="warning" sx={{ mt: 1 }}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    This debit was written off
                                  </Typography>
                                  <Typography variant="body2">
                                    Amount written off: {formatCurrency(debit.writeOffAmount ?? debit.amount ?? 0)}
                                  </Typography>
                                  {debit.writeOffReason && (
                                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                                      Reason: {debit.writeOffReason}
                                    </Typography>
                                  )}
                                </Alert>
                              </Grid>
                            )}
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">EMI Date</Typography>
                              <Typography variant="body1">{debit.emiDate || 'N/A'}</Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Divider sx={{ my: 1 }} />
                              <Typography variant="body2" color="text.secondary" gutterBottom>
                                Update Debit Status
                              </Typography>
                              <FormControl fullWidth size="small">
                                <Select
                                  value={debit.status}
                                  onChange={(e) => handleStatusUpdate(debit.id, e.target.value, debit.status)}
                                  disabled={
                                    updatingStatus[debit.id] ||
                                    (debit.status || '').toUpperCase() === 'WRITTEN_OFF'
                                  }
                                  sx={{ bgcolor: 'background.paper' }}
                                >
                                  <MenuItem value="OPEN">Open</MenuItem>
                                  <MenuItem value="CLOSED">Closed</MenuItem>
                                  <MenuItem value="CREATED">Created</MenuItem>
                                  <MenuItem value="ACTIVE">Active</MenuItem>
                                  <MenuItem value="INACTIVE">Inactive</MenuItem>
                                  <MenuItem value="COMPLETED">Completed</MenuItem>
                                </Select>
                              </FormControl>
                              {(debit.status || '').toUpperCase() === 'WRITTEN_OFF' && (
                                <Typography variant="caption" color="text.secondary">
                                  Written-off debits cannot be changed here; use write-off flow only.
                                </Typography>
                              )}
                              {updatingStatus[debit.id] && (
                                <LinearProgress sx={{ mt: 1 }} />
                              )}
                            </Grid>
                            <Grid item xs={12}>
                              <Typography variant="body2" color="text.secondary" gutterBottom>
                                Payment Progress
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ flexGrow: 1 }}>
                                  <LinearProgress 
                                    variant="determinate" 
                                    value={calculateProgress(debit.outstanding, debit.amount)}
                                    sx={{ height: 8, borderRadius: 4 }}
                                  />
                                </Box>
                                <Typography variant="body2">
                                  {calculateProgress(debit.outstanding, debit.amount).toFixed(1)}%
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined" sx={{ bgcolor: 'action.hover' }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6">Capture Payment</Typography>
                            <AccountBalance color="primary" />
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Record a new payment for this debit
                          </Typography>
                          <Button
                            variant="contained"
                            fullWidth
                            startIcon={<Payment />}
                            onClick={() => handleOpenPaymentDialog(debit.id)}
                            size="large"
                            disabled={(debit.status || '').toUpperCase() === 'WRITTEN_OFF'}
                          >
                            Add Payment
                          </Button>
                          {(debit.status || '').toUpperCase() === 'WRITTEN_OFF' && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                              No further payments on written-off debits.
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  {/* Payment History */}
                  <Box>
                    <Typography variant="h6" gutterBottom>Payment History</Typography>
                    {loadingPayments[debit.id] ? (
                      <LinearProgress />
                    ) : payments[debit.id] && payments[debit.id].length > 0 ? (
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Date</TableCell>
                              <TableCell>Amount</TableCell>
                              <TableCell>Type</TableCell>
                              <TableCell>Received By</TableCell>
                              <TableCell>Note</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {payments[debit.id].map((payment, idx) => (
                              <TableRow key={idx} hover>
                                <TableCell>
                                  {payment.createdDate ? new Date(payment.createdDate).toLocaleDateString() : 
                                   payment.createdAt ? new Date(payment.createdAt).toLocaleDateString() : 'N/A'}
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="success.main" fontWeight="bold">
                                    {formatCurrency(payment.amount || 0)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip label={payment.paymentType || 'N/A'} size="small" />
                                </TableCell>
                                <TableCell>{payment.paymentReceivedBy || 'N/A'}</TableCell>
                                <TableCell>{payment.note || '-'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Alert severity="info">No payments recorded yet for this debit.</Alert>
                    )}
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
              ))}
              </Box>
              {/* Pagination Controls */}
              {totalDebits > 0 && (
                <Paper sx={{ mt: 2 }}>
                  <TablePagination
                    component="div"
                    count={totalDebits}
                    page={debitPage}
                    onPageChange={handleDebitPageChange}
                    rowsPerPage={debitRowsPerPage}
                    onRowsPerPageChange={handleDebitRowsPerPageChange}
                    rowsPerPageOptions={[10, 20, 50, 100]}
                  />
                </Paper>
              )}
            </>
          )}
        </Box>
      )}

      {/* Tab Panel: All Payment History */}
      {tabValue === 1 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">All Payment History</Typography>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchAllPayments}
              disabled={loadingAllPayments}
              size="small"
            >
              Refresh
            </Button>
          </Box>

          {loadingAllPayments ? (
            <LinearProgress sx={{ mb: 2 }} />
          ) : allPayments.length === 0 ? (
            <Alert severity="info">
              No payment records found.
            </Alert>
          ) : (
            <Paper>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Payment ID</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Debit ID</TableCell>
                      <TableCell>Received By</TableCell>
                      <TableCell>Note</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {allPayments.map((payment) => (
                      <TableRow key={payment.id || payment.paymentId} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {(payment.id || payment.paymentId || '').substring(0, 8)}...
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {payment.createdDate ? new Date(payment.createdDate).toLocaleDateString() : 
                           payment.createdAt ? new Date(payment.createdAt).toLocaleDateString() : 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="success.main" fontWeight="bold">
                            {formatCurrency(payment.amount || 0)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={payment.paymentType || 'N/A'} 
                            size="small"
                            color={
                              payment.paymentType === 'REPAYMENT' ? 'success' : 
                              payment.paymentType === 'TOP_UP' ? 'info' : 
                              'default'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {(payment.debitId || 'N/A').substring(0, 8)}...
                          </Typography>
                        </TableCell>
                        <TableCell>{payment.paymentReceivedBy || 'N/A'}</TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                            {payment.note || '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={totalPayments}
                page={paymentPage}
                onPageChange={handleChangePage}
                rowsPerPage={paymentRowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </Paper>
          )}
        </Box>
      )}

      {/* Payment Capture Dialog */}
      <Dialog
        open={openPaymentDialog}
        onClose={handleClosePaymentDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Capture Payment</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth required>
              <InputLabel>Payment Type</InputLabel>
              <Select
                name="paymentType"
                value={paymentForm.paymentType}
                label="Payment Type"
                onChange={handlePaymentFormChange as any}
              >
                <MenuItem value="TOP_UP">Topup Credit</MenuItem>
                <MenuItem value="REPAYMENT">Repayment</MenuItem>
                <MenuItem value="INTEREST">Interest Payment</MenuItem>
              </Select>
            </FormControl>
            <AmountInput
              fullWidth
              label="Amount"
              name="amount"
              value={paymentForm.amount}
              onChange={handlePaymentFormChange}
              placeholder="Enter payment amount"
              required
              inputProps={{ step: "0.01", min: "0" }}
            />
            <TextField
              fullWidth
              label="Note"
              name="note"
              value={paymentForm.note}
              onChange={handlePaymentFormChange}
              placeholder="Add any additional notes"
              multiline
              rows={3}
            />
            <TextField
              fullWidth
              label="Received By"
              name="paymentReceivedBy"
              value={paymentForm.paymentReceivedBy}
              onChange={handlePaymentFormChange}
              placeholder="Enter receiver name"
            />
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Credit score (0 = worst, 5 = good)
              </Typography>
              <Slider
                value={paymentForm.creditScore === '' ? 0 : Number(paymentForm.creditScore)}
                min={0}
                max={5}
                step={1}
                marks={[
                  { value: 0, label: '0' },
                  { value: 1, label: '1' },
                  { value: 2, label: '2' },
                  { value: 3, label: '3' },
                  { value: 4, label: '4' },
                  { value: 5, label: '5' },
                ]}
                valueLabelDisplay="auto"
                onChange={(_, value) => setPaymentForm(prev => ({ ...prev, creditScore: String(Array.isArray(value) ? value[0] : value) }))}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            variant="outlined" 
            color="warning"
            onClick={handleClosePaymentDialog} 
            disabled={loading}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="success"
            onClick={handleSubmitPayment}
            disabled={loading || !paymentForm.amount || !paymentForm.paymentType}
          >
            Submit Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Outstanding Report Preview Dialog */}
      <Dialog
        open={reportPreviewOpen}
        onClose={() => setReportPreviewOpen(false)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>
          {customer
            ? `Customer Outstanding Report — ${customer.name}`
            : 'Customer Outstanding Report'}
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          {!customer && <Typography variant="body2">{reportText}</Typography>}
          {customer && !debits.length && (
            <Alert severity="info">{reportText}</Alert>
          )}
          {customer && debits.length > 0 && outstandingReportHeader && (
            <Stack spacing={2}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Total debit amount
                    </Typography>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {formatCurrency(outstandingReportHeader.totalAmount)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Current outstanding
                    </Typography>
                    <Typography variant="subtitle1" fontWeight={600} color="error.main">
                      {formatCurrency(outstandingReportHeader.totalOutstandingLocal)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Total expected monthly interest
                    </Typography>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {formatCurrency(outstandingReportHeader.totalExpectedInterest)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Nearest maturity
                    </Typography>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {outstandingReportHeader.nearestDueDateStr}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Interest rate(s) on file
                    </Typography>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {outstandingReportHeader.ratesStr}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Typography variant="subtitle2" color="text.secondary">
                Debit-wise breakdown
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 520 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell align="center" sx={{ fontWeight: 600, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                        #
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                        Debit ID
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                        Total
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                        Outstanding
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                        Exp. int/mo
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                        Int %
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                        Pay day
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                        Next EMI
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                        Maturity
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                        Status
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {debits.map((debit, index) => {
                      const r = mapDebitToReportRow(debit, index);
                      return (
                        <TableRow key={debit.id} hover>
                          <TableCell align="center">{r.rowNum}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>{r.idShort}</TableCell>
                          <TableCell align="right">{formatCurrency(r.amount)}</TableCell>
                          <TableCell align="right">{formatCurrency(r.outstanding)}</TableCell>
                          <TableCell align="right">{formatCurrency(r.expInt)}</TableCell>
                          <TableCell align="center">{r.intLabel}</TableCell>
                          <TableCell align="center">{r.payDay}</TableCell>
                          <TableCell align="center">{r.nextEmi}</TableCell>
                          <TableCell align="center">{r.maturity}</TableCell>
                          <TableCell align="center">{r.status}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            startIcon={<ContentCopy />}
            onClick={handleCopyOutstandingReport}
          >
            Copy
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Share />}
            onClick={handleShareOutstandingReport}
          >
            Share
          </Button>
          <Button onClick={() => setReportPreviewOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Convert Short-Term Debit to Interest Dialog */}
      <Dialog
        open={convertDialogOpen}
        onClose={handleCloseConvertDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Convert to Interest Debit</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Interest Rate (%)"
              name="interestRate"
              type="number"
              value={convertForm.interestRate}
              onChange={handleConvertFormChange}
              fullWidth
              inputProps={{ step: '0.1', min: '0' }}
            />
            <TextField
              label="New Start Date"
              name="startDate"
              type="date"
              value={convertForm.startDate}
              onChange={handleConvertFormChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="Optional: adjust start date for interest calculation"
            />
            <TextField
              label="New End Date"
              name="endDate"
              type="date"
              value={convertForm.endDate}
              onChange={handleConvertFormChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="Optional: adjust end date"
            />
            <TextField
              label="Payment Date (1-31)"
              name="paymentDate"
              type="number"
              value={convertForm.paymentDate}
              onChange={handleConvertFormChange}
              fullWidth
              inputProps={{ min: '1', max: '31' }}
              helperText="Optional: monthly payment day"
            />
            {convertDebitAmount !== null && convertForm.interestRate && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Estimated Monthly Interest
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {formatCurrency(
                    (convertDebitAmount * parseFloat(convertForm.interestRate || '0')) / 100
                  )}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Formula: (amount / 100) × interest %
                </Typography>
              </Box>
            )}
            <TextField
              label="Conversion Comment"
              name="conversionComment"
              value={convertForm.conversionComment}
              onChange={handleConvertFormChange}
              fullWidth
              multiline
              rows={3}
              placeholder="Reason for converting short-term debit to interest debit"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConvertDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmitConvert}>
            Convert
          </Button>
        </DialogActions>
      </Dialog>

      {/* Write off debit (dead-end loan) */}
      <Dialog open={writeOffDialogOpen} onClose={handleCloseWriteOff} maxWidth="sm" fullWidth>
        <DialogTitle>Write off debit</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2, mt: 1 }}>
            This sets the debit to <strong>WRITTEN_OFF</strong>, records the current outstanding (
            {formatCurrency(writeOffOutstanding)}) as written-off debt, and zeros the outstanding balance.
            This cannot be undone from the UI.
          </Alert>
          <TextField
            label="Reason (required)"
            value={writeOffForm.reason}
            onChange={(e) => setWriteOffForm((p) => ({ ...p, reason: e.target.value }))}
            fullWidth
            required
            multiline
            minRows={3}
            placeholder="e.g. Customer unresponsive, legal dead-end, settlement failed"
          />
          <FormControlLabel
            sx={{ mt: 2 }}
            control={
              <Checkbox
                checked={writeOffForm.blacklistBorrower}
                onChange={(e) =>
                  setWriteOffForm((p) => ({ ...p, blacklistBorrower: e.target.checked }))
                }
              />
            }
            label="Also blacklist this customer (credit status BLOCKED)"
          />
          {writeOffForm.blacklistBorrower && (
            <TextField
              label="Blacklist note (optional)"
              value={writeOffForm.blacklistReason}
              onChange={(e) => setWriteOffForm((p) => ({ ...p, blacklistReason: e.target.value }))}
              fullWidth
              sx={{ mt: 2 }}
              helperText="Defaults to write-off reason if left blank"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseWriteOff} disabled={writeOffSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleSubmitWriteOff}
            disabled={writeOffSubmitting || !writeOffForm.reason.trim()}
          >
            {writeOffSubmitting ? 'Processing…' : 'Confirm write-off'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Debit History Dialog */}
      <Dialog
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Debit History</DialogTitle>
        <DialogContent dividers>
          {historyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <LinearProgress sx={{ width: '100%' }} />
            </Box>
          ) : historyEvents.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No history available for this debit.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {historyEvents.map((event, index) => (
                <Box key={index} sx={{ p: 1.5, borderRadius: 1, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="body2" fontWeight="bold">
                    {event.description}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {event.eventDate
                      ? new Date(event.eventDate).toLocaleString()
                      : 'Date not available'}
                  </Typography>
                  {event.amount != null && (
                    <Typography variant="body2">
                      Amount: {formatCurrency(event.amount)}
                    </Typography>
                  )}
                  {event.paymentType && (
                    <Typography variant="caption" color="text.secondary">
                      Payment Type: {event.paymentType}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Capture Debit Modal */}
      {customerId && (
        <DebitCaptureModal
          open={openCaptureDebitDialog}
          borrowerId={customerId}
          handleDebitModelClose={handleCloseCaptureDebitDialog}
          onDebitCreated={handleDebitCreated}
        />
      )}
    </Box>
  );
};

export default CustomerDebitsView;

